"use client";

import Link from "next/link";
import { useQuery } from "convex/react";
import { useSearchParams } from "next/navigation";
import { api } from "../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { SubscriptionStatusPanel } from "@/components/dashboard/SubscriptionStatusPanel";
import { VultrServerPanel } from "@/components/dashboard/VultrServerPanel";
import { OnboardingChecklist } from "@/components/dashboard/OnboardingChecklist";
import { useSubscription } from "@/hooks/useSubscription";
import { useCheckout } from "@/hooks/useCheckout";
import { cn } from "@/lib/utils";
import { usePageTitle } from "@/lib/usePageTitle";
import {
  OrganizationsIcon,
  UsersIcon,
  PlusIcon,
  ArrowRightIcon,
  OnlineIcon,
  ChatIcon,
  TrendingIcon,
  BillingIcon,
  ServerIcon,
  LockIcon,
} from "@/components/icons";

export default function DashboardPage() {
  usePageTitle("Dashboard");
  const searchParams = useSearchParams();
  const showSuccess =
    searchParams.get("subscription") === "success" ||
    searchParams.get("checkout") === "success";

  const ownedOrgs = useQuery(api.organizations.getOwned);
  const joinedOrgsRaw = useQuery(api.organizations.getJoined);

  const joinedOrgs =
    ownedOrgs !== undefined && joinedOrgsRaw !== undefined
      ? joinedOrgsRaw.filter(
          (org) => !ownedOrgs.some((owned) => owned._id === org._id)
        )
      : undefined;

  const combinedOrgs =
    ownedOrgs !== undefined && joinedOrgs !== undefined
      ? [...ownedOrgs, ...joinedOrgs]
      : undefined;

  const totalMembers =
    combinedOrgs?.reduce((sum, org) => sum + org.memberCount, 0) ?? undefined;
  const totalOnline =
    combinedOrgs?.reduce((sum, org) => sum + org.onlineCount, 0) ?? undefined;
  const onlineRate =
    totalMembers && totalOnline !== undefined && totalMembers > 0
      ? Number(((totalOnline / totalMembers) * 100).toFixed(1))
      : undefined;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
        <p className="mt-1 text-muted">
          Welcome back! Here&apos;s what&apos;s happening with your communities.
        </p>
      </div>

      <OnboardingChecklist />

      {/* Subscription status widget — shows trial days, upgrade CTA, success confirmation */}
      <div className="mb-8">
        <SubscriptionStatusPanel showSuccess={showSuccess} />
      </div>

      <VultrServerPanel />

      {/* Quick Stats */}
      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <QuickStatCard
          title="My Organizations"
          value={ownedOrgs?.length}
          icon={<OrganizationsIcon className="h-5 w-5" />}
          info="Organizations you own and manage."
        />
        <QuickStatCard
          title="Joined"
          value={joinedOrgs?.length}
          icon={<UsersIcon className="h-5 w-5" />}
          info="Organizations you're a member of (excluding ones you own)."
        />
        <QuickStatCard
          title="Organization Users"
          value={totalMembers}
          icon={<TrendingIcon className="h-5 w-5" />}
          info="Total members across your owned and joined organizations."
        />
        <QuickStatCard
          title="Online Members"
          value={totalOnline}
          icon={<OnlineIcon className="h-5 w-5" />}
          info="Members currently online across your organizations."
          subValue={
            onlineRate === undefined ? undefined : `${onlineRate.toFixed(1)}% online rate`
          }
          highlight
        />
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="mb-4 text-lg font-semibold text-foreground">
          Quick Actions
        </h2>
        <div className="flex flex-wrap gap-4">
          <Link href="/dashboard/my-orgs/new">
            <Button>
              <PlusIcon className="mr-2 h-4 w-4" />
              Create Organization
            </Button>
          </Link>
          <Link href="/servers">
            <Button variant="outline">
              <ChatIcon className="mr-2 h-4 w-4" />
              Browse Servers
            </Button>
          </Link>
          <Link href="/dashboard/servers">
            <Button variant="outline">
              <ServerIcon className="mr-2 h-4 w-4" />
              My Servers
            </Button>
          </Link>
          <Link href="/dashboard/billing">
            <Button variant="outline">
              <BillingIcon className="mr-2 h-4 w-4" />
              Manage Billing
            </Button>
          </Link>
        </div>
      </div>

      {/* Premium Features — gated with lock icon when no active subscription */}
      <PremiumFeaturesSection />

      {/* Organizations Grid */}
      <div className="grid gap-8 lg:grid-cols-2">
        {/* My Organizations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              My Organizations
              <InfoHint text="Organizations you own and manage." />
            </CardTitle>
            <Link href="/dashboard/my-orgs">
              <Button variant="ghost" size="sm">
                View All
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {ownedOrgs === undefined ? (
              <div className="space-y-3">
                <Skeleton className="h-16" />
                <Skeleton className="h-16" />
              </div>
            ) : ownedOrgs.length === 0 ? (
              <div className="py-8 text-center">
                <OrganizationsIcon className="mx-auto h-8 w-8 text-muted" />
                <p className="mt-2 text-sm text-muted">
                  No organizations yet
                </p>
                <Link href="/dashboard/my-orgs/new" className="mt-4 inline-block">
                  <Button size="sm">Create One</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {ownedOrgs.slice(0, 3).map((org) => (
                  <Link
                    key={org._id}
                    href={`/org/${org.slug}`}
                    className="flex items-center justify-between rounded-lg border border-border p-3 transition-all hover:bg-foreground/[0.03] hover:border-foreground/15 hover:shadow-sm"
                  >
                    <div>
                      <p className="font-medium text-foreground">{org.name}</p>
                      <p className="text-sm text-muted">
                        {org.memberCount} members
                      </p>
                    </div>
                    <Badge variant="success">
                      <OnlineIcon className="mr-1 h-3 w-3" />
                      {org.onlineCount}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Joined Organizations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              Joined Organizations
              <InfoHint text="Organizations you are a member of, excluding ones you own." />
            </CardTitle>
            <Link href="/dashboard/joined">
              <Button variant="ghost" size="sm">
                View All
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {joinedOrgs === undefined ? (
              <div className="space-y-3">
                <Skeleton className="h-16" />
                <Skeleton className="h-16" />
              </div>
            ) : joinedOrgs.length === 0 ? (
              <div className="py-8 text-center">
                <UsersIcon className="mx-auto h-8 w-8 text-muted" />
                <p className="mt-2 text-sm text-muted">
                  No organizations joined
                </p>
                <Link href="/servers" className="mt-4 inline-block">
                  <Button size="sm" variant="outline">
                    Browse Servers
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {joinedOrgs.slice(0, 3).map((org) => (
                  <Link
                    key={org._id}
                    href={`/org/${org.slug}`}
                    className="flex items-center justify-between rounded-lg border border-border p-3 transition-all hover:bg-foreground/[0.03] hover:border-foreground/15 hover:shadow-sm"
                  >
                    <div>
                      <p className="font-medium text-foreground">{org.name}</p>
                      <div className="flex items-center gap-2 text-sm text-muted">
                        <span>{org.memberCount} members</span>
                        <Badge variant="outline" className="text-xs">
                          {org.memberRole}
                        </Badge>
                      </div>
                    </div>
                    <Badge variant="success">
                      <OnlineIcon className="mr-1 h-3 w-3" />
                      {org.onlineCount}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Premium Features Section ─────────────────────────────────────────────────

/**
 * PremiumFeaturesSection
 *
 * Shows a grid of premium features available on higher tiers. When the user
 * has no active subscription (or their trial has expired), each locked feature
 * displays a lock icon overlay with an upgrade prompt. When the user has
 * access but is on an insufficient tier for a specific feature, a tier badge
 * is shown instead.
 */
function PremiumFeaturesSection() {
  const { hasAccess, featureFlags, isLoading, isAdmin, hasUsedTrial } = useSubscription();
  const { startCheckout, loadingPlan } = useCheckout();

  if (isLoading) return null;

  const premiumFeatures = [
    {
      label: "Advanced Stats",
      description: "Deep analytics across all your organizations and server networks.",
      href: "/dashboard/advanced-stats",
      icon: <TrendingIcon className="h-5 w-5" />,
      unlocked: featureFlags.canViewAdvancedStats || isAdmin,
      requiredTierLabel: "Plus",
    },
    {
      label: "Priority Queue",
      description: "Give your subscribers faster join times during peak periods.",
      href: "/dashboard/priority-queue",
      icon: <ServerIcon className="h-5 w-5" />,
      unlocked: featureFlags.canAccessPriorityQueue || isAdmin,
      requiredTierLabel: "Pro",
    },
  ];

  // Don't render the section if all features are unlocked
  const allUnlocked = premiumFeatures.every((f) => f.unlocked);
  if (allUnlocked) return null;

  const handleUpgradeCTA = () =>
    startCheckout("plus", {
      withTrial: !hasUsedTrial && !hasAccess,
      successRedirect: "/dashboard?subscription=success",
    });

  return (
    <div className="mb-8">
      <h2 className="mb-4 text-lg font-semibold text-foreground">
        Premium Features
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        {premiumFeatures.map((feature) => (
          <PremiumFeatureCard
            key={feature.href}
            label={feature.label}
            description={feature.description}
            href={feature.href}
            icon={feature.icon}
            unlocked={feature.unlocked}
            hasAccess={hasAccess}
            requiredTierLabel={feature.requiredTierLabel}
            onUpgrade={handleUpgradeCTA}
            upgradeLoading={loadingPlan !== null}
          />
        ))}
      </div>
    </div>
  );
}

function PremiumFeatureCard({
  label,
  description,
  href,
  icon,
  unlocked,
  hasAccess,
  requiredTierLabel,
  onUpgrade,
  upgradeLoading,
}: {
  label: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  unlocked: boolean;
  hasAccess: boolean;
  requiredTierLabel: string;
  onUpgrade: () => void;
  upgradeLoading: boolean;
}) {
  // Fully unlocked — render as a normal navigable card
  if (unlocked) {
    return (
      <Link href={href}>
        <div className="flex items-start gap-4 rounded-lg border border-border bg-background p-4 transition-all hover:border-primary/30 hover:bg-foreground/[0.02] hover:shadow-sm">
          <div className="rounded-lg bg-primary/10 p-2 text-primary">{icon}</div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground">{label}</p>
            <p className="mt-0.5 text-sm text-muted line-clamp-2">{description}</p>
          </div>
          <ArrowRightIcon className="h-4 w-4 shrink-0 text-muted mt-1" />
        </div>
      </Link>
    );
  }

  // Has an active subscription but not the right tier — show tier requirement
  if (hasAccess) {
    return (
      <Link href={href}>
        <div className="flex items-start gap-4 rounded-lg border border-border bg-background p-4 transition-all hover:border-primary/30 hover:bg-foreground/[0.02] hover:shadow-sm">
          <div className="rounded-lg bg-foreground/5 p-2 text-muted">{icon}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium text-foreground">{label}</p>
              <Badge variant="outline" className="text-xs">
                {requiredTierLabel}+
              </Badge>
            </div>
            <p className="mt-0.5 text-sm text-muted line-clamp-2">{description}</p>
          </div>
          <ArrowRightIcon className="h-4 w-4 shrink-0 text-muted mt-1" />
        </div>
      </Link>
    );
  }

  // No active subscription — show lock overlay with upgrade prompt
  return (
    <div className="relative overflow-hidden rounded-lg border border-border bg-background p-4">
      {/* Lock overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-background/90 backdrop-blur-[1px]">
        <div className="rounded-full bg-foreground/10 p-2">
          <LockIcon className="h-5 w-5 text-muted" />
        </div>
        <p className="text-xs font-medium text-muted">
          Requires an active subscription
        </p>
        <Button
          size="sm"
          className="mt-1 h-7 px-3 text-xs"
          onClick={onUpgrade}
          disabled={upgradeLoading}
        >
          {upgradeLoading ? "Loading..." : "Upgrade to Unlock"}
        </Button>
      </div>

      {/* Blurred feature preview */}
      <div className="flex items-start gap-4 select-none opacity-30 blur-[2px]">
        <div className="rounded-lg bg-foreground/5 p-2 text-muted">{icon}</div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground">{label}</p>
          <p className="mt-0.5 text-sm text-muted line-clamp-2">{description}</p>
        </div>
      </div>
    </div>
  );
}

// ─── QuickStatCard ────────────────────────────────────────────────────────────

function QuickStatCard({
  title,
  value,
  icon,
  info,
  subValue,
  highlight = false,
}: {
  title: string;
  value: number | undefined;
  icon: React.ReactNode;
  info: string;
  subValue?: string;
  highlight?: boolean;
}) {
  return (
    <Card className={highlight ? "border-primary" : ""}>
      <CardContent className="flex items-center gap-4 p-4">
        <div
          className={cn(
            "rounded-lg p-2",
            highlight ? "bg-primary/10 text-primary" : "bg-foreground/5 text-muted"
          )}
        >
          {icon}
        </div>
        <div>
          {value === undefined ? (
            <Skeleton className="h-6 w-12" />
          ) : (
            <p className={cn("text-2xl font-bold", highlight && "text-primary")}>
              {value}
            </p>
          )}
          <div className="flex items-center gap-1 text-sm text-muted">
            <span>{title}</span>
            <InfoHint text={info} />
          </div>
          {subValue && <p className="text-xs text-muted">{subValue}</p>}
        </div>
      </CardContent>
    </Card>
  );
}
